import { useEffect, useMemo, useState } from "react";
import { Search, X, Mail } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled"];

const STATUS_COLORS = {
    pending: "text-neutral-700 border-neutral-300 bg-neutral-100",
    confirmed: "text-neutral-900 border-neutral-300 bg-neutral-100",
    processing: "text-amber-700 border-amber-400/30 bg-amber-400/10",
    shipped: "text-violet-300 border-violet-400/30 bg-violet-400/10",
    delivered: "text-emerald-700 border-emerald-400/30 bg-emerald-400/10",
    cancelled: "text-red-700 border-red-400/30 bg-red-400/10",
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.status_filter = filter;
            if (query.trim()) params.q = query.trim();
            const { data } = await api.get("/admin/orders", { params });
            setOrders(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const fetchOrders = async () => {
            try {
                const params = {};
                if (filter !== "all") params.status_filter = filter;
                const { data } = await api.get("/admin/orders", { params });
                if (!cancelled) {
                    setOrders(data);
                    setLoading(false);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    setLoading(false);
                }
            }
        };
        fetchOrders();
        return () => {
            cancelled = true;
        };
    }, [filter]);

    const selected = useMemo(() => orders.find((o) => o.id === selectedId), [orders, selectedId]);

    const changeStatus = async (orderId, newStatus) => {
        setSavingId(orderId);
        try {
            const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
            setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-orders">
            <div className="mb-6 sm:mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">// Orders</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    Order management
                </h1>
            </div>

            <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 lg:gap-4 mb-6">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        load();
                    }}
                    className="relative w-full lg:flex-1 lg:min-w-[260px] lg:max-w-md"
                >
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by order id, email, or recipient"
                        className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-10 pr-3 py-2.5 text-sm"
                        data-testid="orders-search"
                    />
                </form>
                <div className="flex flex-wrap gap-2 -mx-1 px-1 overflow-x-auto" data-testid="orders-status-filter">
                    {["all", ...STATUSES].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`flex-shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg ${
                                filter === s
                                    ? "bg-[#0089c8] text-white border-[#0089c8]"
                                    : "border-neutral-200 text-neutral-600 hover:border-[#0089c8] hover:text-black"
                            }`}
                            data-testid={`filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3 mb-4 rounded-lg" data-testid="orders-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-neutral-500 text-sm">Loading orders…</div>
            ) : orders.length === 0 ? (
                <div className="border border-neutral-200 rounded-xl p-10 text-center text-neutral-500" data-testid="orders-empty">
                    No orders match the current filter.
                </div>
            ) : (
                <>
                    {/* Mobile: card layout (<md) */}
                    <div className="md:hidden space-y-3">
                        {orders.map((o) => (
                            <div
                                key={o.id}
                                className="border border-neutral-200 rounded-lg p-4 bg-white"
                                data-testid={`order-row-${o.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-mono text-[#0077b6] text-xs truncate">{o.id}</div>
                                        <div className="text-sm text-neutral-800 mt-1 truncate">{o.shipping?.full_name || "—"}</div>
                                        <div className="text-[10px] text-neutral-500 truncate">{o.email}</div>
                                        <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                                            {new Date(o.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="font-display font-bold text-lg">£{o.total.toFixed(2)}</div>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <select
                                        value={o.status}
                                        onChange={(e) => changeStatus(o.id, e.target.value)}
                                        disabled={savingId === o.id}
                                        className={`bg-transparent border rounded-lg px-2 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold focus:outline-none focus:border-[#00aeef] ${
                                            STATUS_COLORS[o.status] || STATUS_COLORS.pending
                                        }`}
                                        data-testid={`status-select-${o.id}`}
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s} className="bg-[#f8f8f8]">{s}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedId(o.id)}
                                        className="text-[10px] uppercase tracking-[0.18em] text-[#0077b6] hover:underline"
                                        data-testid={`view-order-${o.id}`}
                                    >
                                        View →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: table (≥md) */}
                    <div className="hidden md:block border border-neutral-200 rounded-xl overflow-hidden overflow-x-auto">
                        <table className="w-full text-sm min-w-[640px]">
                        <thead className="bg-[#f8f8f8] text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                            <tr>
                                <th className="text-left px-4 py-3">Order ID</th>
                                <th className="text-left px-4 py-3 hidden md:table-cell">Date</th>
                                <th className="text-left px-4 py-3 hidden md:table-cell">Customer</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-right px-4 py-3">Total</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr
                                    key={o.id}
                                    className="border-t border-neutral-200 hover:bg-neutral-100/[0.02]"
                                >
                                    <td className="px-4 py-3 font-mono text-[#0077b6]">{o.id}</td>
                                    <td className="px-4 py-3 hidden md:table-cell text-neutral-600 font-mono text-xs">
                                        {new Date(o.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell text-neutral-700">
                                        <div>{o.shipping?.full_name || "—"}</div>
                                        <div className="text-xs text-neutral-500">{o.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={o.status}
                                            onChange={(e) => changeStatus(o.id, e.target.value)}
                                            disabled={savingId === o.id}
                                            className={`bg-transparent border rounded-lg px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold focus:outline-none focus:border-[#00aeef] ${
                                                STATUS_COLORS[o.status] || STATUS_COLORS.pending
                                            }`}
                                        >
                                            {STATUSES.map((s) => (
                                                <option key={s} value={s} className="bg-[#f8f8f8]">{s}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono">£{o.total.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedId(o.id)}
                                            className="text-xs uppercase tracking-[0.18em] text-neutral-600 hover:text-black"
                                        >
                                            View →
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </>
            )}

            {selected && (
                <OrderDrawer order={selected} onClose={() => setSelectedId(null)} onStatusChange={changeStatus} savingId={savingId} />
            )}
        </div>
    );
}

function OrderDrawer({ order, onClose, onStatusChange, savingId }) {
    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-white/70 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            data-testid="order-drawer"
        >
            <button
                type="button"
                className="absolute inset-0"
                aria-label="Close drawer"
                onClick={onClose}
                data-testid="order-drawer-backdrop"
            />
            <aside className="relative w-full max-w-2xl bg-white border-l border-neutral-200 overflow-y-auto">
                <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-neutral-200 sticky top-0 bg-white z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6]">// Order</div>
                        <div className="font-display font-bold text-base sm:text-lg font-mono truncate">{order.id}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:text-black flex-shrink-0"
                        data-testid="order-drawer-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Status</div>
                        <div className="flex flex-wrap gap-2" data-testid="drawer-statuses">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => onStatusChange(order.id, s)}
                                    disabled={savingId === order.id}
                                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg ${
                                        order.status === s
                                            ? STATUS_COLORS[s]
                                            : "border-neutral-200 text-neutral-600 hover:border-[#0089c8] hover:text-black"
                                    }`}
                                    data-testid={`drawer-status-${s}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Status changes trigger a (currently mocked) email notification.
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-xs">
                        <Field label="Created">{new Date(order.created_at).toLocaleString()}</Field>
                        <Field label="Total">£{order.total.toFixed(2)}</Field>
                        <Field label="Subtotal">£{order.subtotal.toFixed(2)}</Field>
                        <Field label="Shipping">{order.shipping_cost === 0 ? "Free" : `£${order.shipping_cost.toFixed(2)}`}</Field>
                        <Field label="Tax">£{order.tax.toFixed(2)}</Field>
                        <Field label="Payment">{order.payment_summary?.brand} ····{order.payment_summary?.last4}</Field>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Shipping</div>
                        <div className="text-sm text-neutral-800 leading-relaxed border border-neutral-200 rounded-lg p-4">
                            {order.shipping?.full_name}
                            <br />
                            {order.shipping?.address}
                            <br />
                            {order.shipping?.city}, {order.shipping?.state} {order.shipping?.zip}
                            <br />
                            {order.shipping?.country}
                            <br />
                            <span className="text-neutral-500">{order.email}</span>
                            {order.shipping?.phone && (
                                <>
                                    <br />
                                    <span className="text-neutral-500">{order.shipping.phone}</span>
                                </>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Items</div>
                        <div className="space-y-2">
                            {order.items.map((it, i) => (
                                <div key={`${it.product_id}-${it.dose || i}`} className="flex items-center gap-4 border border-neutral-200 rounded-lg p-3">
                                    <div className="w-12 h-12 bg-white border border-neutral-200 overflow-hidden rounded-md flex-shrink-0">
                                        <img src={imgUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 text-sm">
                                        <div className="font-semibold">{it.name}</div>
                                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                                            {it.dose && (
                                                <span className="font-mono text-[#0077b6] border border-[#00aeef]/30 px-1.5 py-0.5 rounded text-[10px]">{it.dose}</span>
                                            )}
                                            <span>Qty {it.quantity}</span>
                                            <span>· £{it.price.toFixed(2)} each</span>
                                        </div>
                                    </div>
                                    <div className="font-mono text-sm">£{it.line_total.toFixed(2)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">{label}</div>
            <div className="text-neutral-800 font-mono">{children}</div>
        </div>
    );
}
